import { toast } from "react-hot-toast";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { createAxiosInstance } from "api/axiosConfig";
import {
  PATIENT_DOCUMENTS_CONTROLER_URL,
  PATIENT_DOCUMENTS_UPLOAD,
} from "api/Service";
import { validateDocumentForm } from "features/documents/utils/documentValidators";
import { useHandleNetworkError } from "utils/useHandleNetworkError";

export const useDocumentUpload = ({ onSuccess } = {}) => {
  const queryClient = useQueryClient();

  const handleNetworkError = useHandleNetworkError(
    "Error while saving document"
  );

  const tenant = localStorage.getItem("kinesin-tenant");

  const uploadMutation = useMutation({
    mutationFn: async ({ imageFile, formData, patient }) => {
      const isEditMode = !!formData.id;
      const validation = validateDocumentForm(formData, isEditMode);
      if (!validation.isValid) {
        throw new Error(Object.values(validation.errors)[0]);
      }

      const axiosInstance = createAxiosInstance();
      let documentId = formData.id;
      let documentUrl = formData.documentUrl;

      if (imageFile && formData.documentType) {
        const data = new FormData();
        data.append("file", imageFile);
        data.append("name", formData.name);

        const patientId =
          patient?.id || patient?.patient_id || formData.patientId;
        const uploadResponse = await axiosInstance.post(
          `${tenant}${PATIENT_DOCUMENTS_UPLOAD}/${formData.documentType}/patient/${patientId}`,
          data
        );

        documentId = uploadResponse.data?.id || documentId;
        documentUrl = uploadResponse.data?.documentUrl || documentUrl;
      }

      const patientId =
        patient?.id || patient?.patient_id || formData.patientId;

      let priorityValue = null;
      if (
        formData.priority !== undefined &&
        formData.priority !== null &&
        formData.priority !== "-1" &&
        formData.priority !== ""
      ) {
        priorityValue = String(formData.priority);
      }

      if (!priorityValue) {
        throw new Error("Priority is required");
      }

      const staffMemberId =
        formData.staffMemberId && formData.staffMemberId !== ""
          ? parseInt(formData.staffMemberId, 10)
          : null;

      const postData = {
        id: documentId,
        tenant: tenant,
        patientId: patientId,
        patient: {
          id: patientId,
          tenant: tenant,
          ...(staffMemberId && {
            doctorDetailsId: staffMemberId,
            doctorDetails: {
              id: staffMemberId,
              tenant: tenant,
            },
          }),
        },
        ...(staffMemberId && {
          reviewerStaffId: staffMemberId,
          reviewedById: staffMemberId,
        }),
        documentTypeId: formData.documentType,
        documentTypes: {
          id: formData.documentType,
          tenant: tenant,
        },
        status: formData.status || "NEW",
        priority: priorityValue,
        name: formData.name,
        documentUrl: documentUrl,
      };

      const response = await axiosInstance.post(
        `${tenant}${PATIENT_DOCUMENTS_CONTROLER_URL}`,
        postData
      );

      return { response, isUpdate: !!formData.id };
    },
    onSuccess: ({ isUpdate }) => {
      toast.success(`Document ${isUpdate ? "updated" : "saved"} successfully`);
      queryClient.invalidateQueries({ queryKey: ["patientDocuments"] });
      onSuccess?.();
    },
    onError: handleNetworkError,
  });

  return {
    mutate: uploadMutation.mutate,
    isPending: uploadMutation.isPending,
    isError: uploadMutation.isError,
    error: uploadMutation.error,
  };
};
